/**
 * Custom hook for timeline zoom functionality
 * Handles zoom in/out, wheel events, pinch gestures, and focus preservation
 */

import { useState, useRef, useCallback, useEffect, useLayoutEffect, type RefObject } from 'react';
import { yearToPixel, pixelToYear } from './timeline-utils';
import { TIMELINE_LAYOUT, getHeaderWidth } from './timeline-config';

interface UseTimelineZoomParams {
    minYear: number;
    maxYear: number;
    timelineContainerRef: RefObject<HTMLDivElement>;
    shouldCenterOnPassageRef: RefObject<boolean>;
}

interface UseTimelineZoomReturn {
    pixelsPerYear: number;
    setPixelsPerYear: (value: number) => void;
    timelineWidth: number;
    onControlZoomIn: () => void;
    onControlZoomOut: () => void;
}

/**
 * Manages all zoom-related functionality for the timeline
 */
export function useTimelineZoom({
    minYear,
    maxYear,
    timelineContainerRef,
    shouldCenterOnPassageRef
}: UseTimelineZoomParams): UseTimelineZoomReturn {
    const [pixelsPerYear, setPixelsPerYear] = useState<number>(TIMELINE_LAYOUT.DEFAULT_ZOOM);
    
    // Calculate timeline width
    const timelineWidth = (maxYear - minYear) * pixelsPerYear;
    
    // Refs for zoom focus management
    const preZoomFocusRef = useRef<{ year: number, screenX: number } | null>(null);
    const zoomGestureAnchorRef = useRef<{ year: number, screenX: number } | null>(null);
    const zoomGestureTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const timelineWidthRef = useRef(timelineWidth);
    
    // Update timelineWidth ref when it changes
    useEffect(() => {
        timelineWidthRef.current = timelineWidth;
    }, [timelineWidth]);

    // Basic zoom handlers
    const handleZoomIn = useCallback((factor: number = 1.5) => {
        shouldCenterOnPassageRef.current = false;
        setPixelsPerYear(prev => {
            const next = prev * factor;
            return Math.min(next, TIMELINE_LAYOUT.MAX_ZOOM);
        });
    }, [shouldCenterOnPassageRef]);

    const handleZoomOut = useCallback((factor: number = 1.5) => {
        shouldCenterOnPassageRef.current = false;
        setPixelsPerYear(prev => {
            const next = prev / factor;
            return Math.max(next, TIMELINE_LAYOUT.MIN_ZOOM);
        });
    }, [shouldCenterOnPassageRef]);

    // Enhanced zoom handlers that store focus point
    const handleZoomInWithFocus = useCallback((screenX?: number, factor: number = 1.5) => {
        const container = timelineContainerRef.current;
        if (!container) {
            handleZoomIn(factor);
            return;
        }

        // Determine screen X position (relative to viewport left edge)
        let visualX: number;
        if (typeof screenX === 'number') {
            visualX = screenX;
        } else {
            visualX = container.clientWidth / 2;
        }

        const headerWidth = getHeaderWidth();
        const scrollLeft = container.scrollLeft;
        
        // If mouse is over the header, clamp to content start
        const effectiveVisualX = Math.max(headerWidth, visualX);
        
        // Calculate year at this position
        const contentX = (scrollLeft + effectiveVisualX) - headerWidth;
        const focusYear = pixelToYear(contentX, minYear, maxYear, timelineWidth);
        
        preZoomFocusRef.current = { year: focusYear, screenX: effectiveVisualX };

        handleZoomIn(factor);
    }, [handleZoomIn, minYear, maxYear, timelineWidth, timelineContainerRef]);

    const handleZoomOutWithFocus = useCallback((screenX?: number, factor: number = 1.5) => {
        const container = timelineContainerRef.current;
        if (!container) {
            handleZoomOut(factor);
            return;
        }

        let visualX: number;
        if (typeof screenX === 'number') {
            visualX = screenX;
        } else {
            visualX = container.clientWidth / 2;
        }

        const headerWidth = getHeaderWidth();
        const scrollLeft = container.scrollLeft;
        const effectiveVisualX = Math.max(headerWidth, visualX);
        
        const contentX = (scrollLeft + effectiveVisualX) - headerWidth;
        const focusYear = pixelToYear(contentX, minYear, maxYear, timelineWidth);
        
        preZoomFocusRef.current = { year: focusYear, screenX: effectiveVisualX };

        handleZoomOut(factor);
    }, [handleZoomOut, minYear, maxYear, timelineWidth, timelineContainerRef]);

    // Restore focus position after zoom
    useLayoutEffect(() => {
        if (preZoomFocusRef.current && timelineContainerRef.current) {
            const { year, screenX } = preZoomFocusRef.current;
            const headerWidth = getHeaderWidth();
            
            // Calculate where the focused year is NOW (in the new timeline width)
            const contentX = yearToPixel(year, minYear, maxYear, timelineWidth);
            
            // We want this year to appear at `screenX` relative to viewport left
            // screenX = (contentX + headerWidth) - scrollLeft
            // Solving for scrollLeft:
            const newScrollLeft = Math.max(0, contentX + headerWidth - screenX);
            
            timelineContainerRef.current.scrollTo({
                left: newScrollLeft,
                behavior: 'instant'
            });
        }
    }, [pixelsPerYear, minYear, maxYear, timelineWidth, timelineContainerRef]);

    // Cleanup effect to clear focus ref after zoom operations settle
    useEffect(() => {
        if (preZoomFocusRef.current) {
            preZoomFocusRef.current = null;
        }
    }, [pixelsPerYear]);

    // Wheel handler (desktop touchpad / ctrl+scroll) with gentle, continuous zoom
    useEffect(() => {
        const container = timelineContainerRef.current;
        if (!container) return;

        const handleWheel = (e: WheelEvent) => {
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();

                const rect = container.getBoundingClientRect();
                const mouseX = e.clientX - rect.left;

                // Clear existing timeout
                if (zoomGestureTimeoutRef.current) {
                    clearTimeout(zoomGestureTimeoutRef.current);
                }

                // Initialize anchor if not present (start of gesture)
                if (!zoomGestureAnchorRef.current) {
                    const headerWidth = getHeaderWidth();
                    const scrollLeft = container.scrollLeft;
                    const effectiveVisualX = Math.max(headerWidth, mouseX);
                    const contentX = (scrollLeft + effectiveVisualX) - headerWidth;
                    const startYear = pixelToYear(contentX, minYear, maxYear, timelineWidthRef.current);
                    
                    zoomGestureAnchorRef.current = { year: startYear, screenX: mouseX };
                } else {
                    // Update screen position (follow mouse) but keep the anchored YEAR
                    zoomGestureAnchorRef.current.screenX = mouseX;
                }
                
                // Push the anchor to the focus ref so layout effect can use it
                preZoomFocusRef.current = { ...zoomGestureAnchorRef.current };
                
                // Exponential mapping from wheel delta to zoom factor
                // Doubled intensity for faster desktop zoom
                const zoomIntensity = 0.0012;
                const clampedDelta = Math.max(-250, Math.min(250, e.deltaY));

                if (Math.abs(clampedDelta) < 0.5) return;

                const factor = Math.exp(Math.abs(clampedDelta) * zoomIntensity);

                if (clampedDelta < 0) {
                    handleZoomIn(factor);
                } else {
                    handleZoomOut(factor);
                }

                // Set timeout to clear anchor when gesture stops
                zoomGestureTimeoutRef.current = setTimeout(() => {
                    zoomGestureAnchorRef.current = null;
                }, 150);
            }
        };

        container.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
            container.removeEventListener('wheel', handleWheel);
            if (zoomGestureTimeoutRef.current) clearTimeout(zoomGestureTimeoutRef.current);
        };
    }, [handleZoomIn, handleZoomOut, minYear, maxYear, timelineContainerRef]);

    // Refs for pinch zoom state (persist across re-renders)
    const lastDistanceRef = useRef(0);
    const pinchCenterXRef = useRef(0);

    // Pinch zoom handler (mobile/trackpad)
    useEffect(() => {
        const container = timelineContainerRef.current;
        if (!container) return;

        const handleTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                const touch1 = e.touches[0];
                const touch2 = e.touches[1];
                lastDistanceRef.current = Math.hypot(
                    touch2.clientX - touch1.clientX,
                    touch2.clientY - touch1.clientY
                );

                const rect = container.getBoundingClientRect();
                pinchCenterXRef.current = ((touch1.clientX + touch2.clientX) / 2) - rect.left;
            }
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                e.preventDefault();

                const touch1 = e.touches[0];
                const touch2 = e.touches[1];
                const currentDistance = Math.hypot(
                    touch2.clientX - touch1.clientX,
                    touch2.clientY - touch1.clientY
                );

                if (lastDistanceRef.current > 0 && currentDistance > 0) {
                    const ratio = currentDistance / lastDistanceRef.current;
                    
                    // Apply zoom more aggressively - use a larger multiplier for mobile
                    // This makes zoom feel more responsive on actual devices
                    const zoomMultiplier = 2.5; // Increase this for faster zoom
                    const adjustedRatio = 1 + (ratio - 1) * zoomMultiplier;
                    
                    if (adjustedRatio > 1) {
                        handleZoomInWithFocus(pinchCenterXRef.current, adjustedRatio);
                    } else {
                        handleZoomOutWithFocus(pinchCenterXRef.current, 1/adjustedRatio);
                    }
                    
                    // Always update lastDistance for continuous zooming
                    lastDistanceRef.current = currentDistance;
                }
            }
        };

        const handleTouchEnd = () => {
            lastDistanceRef.current = 0;
            pinchCenterXRef.current = 0;
        };

        container.addEventListener('touchstart', handleTouchStart, { passive: true });
        container.addEventListener('touchmove', handleTouchMove, { passive: false });
        container.addEventListener('touchend', handleTouchEnd, { passive: true });

        return () => {
            container.removeEventListener('touchstart', handleTouchStart);
            container.removeEventListener('touchmove', handleTouchMove);
            container.removeEventListener('touchend', handleTouchEnd);
        };
    }, [handleZoomInWithFocus, handleZoomOutWithFocus, timelineContainerRef]);

    // Control button handlers (use larger zoom factor for mobile)
    const onControlZoomIn = useCallback(() => handleZoomInWithFocus(undefined, 2.0), [handleZoomInWithFocus]);
    const onControlZoomOut = useCallback(() => handleZoomOutWithFocus(undefined, 2.0), [handleZoomOutWithFocus]);

    return {
        pixelsPerYear,
        setPixelsPerYear,
        timelineWidth,
        onControlZoomIn,
        onControlZoomOut
    };
}

